import React, { useEffect, useRef, useState } from 'react';
import { Bot, Send, Loader2, Trash2, User } from 'lucide-react';
import { Card, Btn } from './shared';
import { api } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const SUGGESTIONS = [
  'Adjust my training if my left knee flares up',
  'Quick high-protein breakfast under 400 kcal',
  'I missed 2 sessions — how do I get back?',
  'How should I deload this week?',
];

const Bubble = ({ m }) => {
  const isUser = m.role === 'user';
  return (
    <div className={`flex gap-2 ${isUser ? 'justify-end' : 'justify-start'}`}>
      {!isUser && (
        <div className="w-8 h-8 rounded-full bg-[#ff7a1a] text-[#0b0b0c] flex items-center justify-center shrink-0 mt-0.5">
          <Bot className="w-4 h-4" />
        </div>
      )}
      <div className={`max-w-[78%] rounded-2xl px-4 py-2.5 leading-relaxed text-sm whitespace-pre-wrap ${
        isUser ? 'bg-[#ff7a1a] text-[#0b0b0c] rounded-br-md' : 'bg-[#0b0c10] border border-[#1f2937] text-[#e6e9f0] rounded-bl-md'
      }`}>
        {m.content}
      </div>
      {isUser && (
        <div className="w-8 h-8 rounded-full bg-[#0b0c10] border border-[#2a2f3a] text-[#cbd5e1] flex items-center justify-center shrink-0 mt-0.5">
          <User className="w-4 h-4" />
        </div>
      )}
    </div>
  );
};

const CoachChat = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const { toast } = useToast();

  useEffect(() => {
    api.coachHistory().then((h) => setMessages(h || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput('');
    const optimistic = { id: 'tmp-' + Date.now(), role: 'user', content: msg, created_at: new Date().toISOString() };
    setMessages((m) => [...m, optimistic]);
    setBusy(true);
    try {
      const r = await api.coachSend(msg);
      setMessages((m) => [...m, { id: r.id, role: 'assistant', content: r.reply, created_at: r.created_at }]);
    } catch (err) {
      toast({ title: 'Coach unavailable', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const clear = async () => {
    if (!window.confirm('Clear coach conversation?')) return;
    try {
      await api.coachClear();
      setMessages([]);
      toast({ title: 'History cleared' });
    } catch (err) {
      toast({ title: 'Failed', description: err.message, variant: 'destructive' });
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <Card
      title="FreeFitFuel Coach" icon={Bot}
      action={messages.length > 0 ? (
        <Btn variant="ghost" onClick={clear}><Trash2 className="w-4 h-4" /> Clear</Btn>
      ) : null}
    >
      <div
        ref={scrollRef}
        className="h-[440px] overflow-y-auto rounded-xl border border-[#1f2937] bg-[#0b0c10] p-4 space-y-3"
      >
        {messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center">
            <div className="w-12 h-12 rounded-full bg-[#ff7a1a] text-[#0b0b0c] flex items-center justify-center">
              <Bot className="w-6 h-6" />
            </div>
            <p className="text-white font-bold mt-3">Ask me anything fitness, nutrition or wellbeing.</p>
            <p className="text-[#aab1bf] text-sm mt-1 max-w-md">I see your plan, check-ins, injuries and equipment. Replies are tailored to you.</p>
            <div className="mt-5 flex flex-wrap gap-2 justify-center">
              {SUGGESTIONS.map((s) => (
                <button key={s} onClick={() => send(s)} className="px-3 py-1.5 rounded-full text-xs font-medium border border-[#2a2f3a] bg-[#0b0c10] text-[#cbd5e1] hover:border-[#ff7a1a]/60 hover:text-white transition-colors">{s}</button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((m) => <Bubble key={m.id} m={m} />)
        )}
        {busy && (
          <div className="flex items-center gap-2 text-[#aab1bf] text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Coach is thinking…
          </div>
        )}
      </div>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-end">
        <textarea
          rows={4} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKey}
          placeholder="Ask about training, food, recovery, anything…"
          data-testid="coach-input"
          className="flex-1 min-h-[110px] bg-[#0b0c10] border border-[#2a2f3a] focus:border-[#ff7a1a] focus:ring-2 focus:ring-[#ff7a1a]/20 rounded-lg px-3 py-3 text-white outline-none text-base resize-y"
        />
        <Btn onClick={() => send()} disabled={busy || !input.trim()} className="sm:self-stretch sm:px-6">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send
        </Btn>
      </div>
    </Card>
  );
};

export default CoachChat;
